import Phaser from "phaser";
import { DynamicScene } from "./DynamicScene";
import { Global } from "../utils/Global";

export class BootScene extends DynamicScene {
  private graphics: Phaser.GameObjects.Graphics = null!;
  private loadingBar: Phaser.GameObjects.Graphics = null!;
  private loadingText: Phaser.GameObjects.BitmapText = null!;
  private versionText: Phaser.GameObjects.BitmapText = null!;
  private loadingProgress: number = 0;

  constructor() {
    super({ key: "BootScene" });
  }

  override init(): void {
    super.init();
  }

  public preload(): void {
    this.load.setCORS("*");

    // Log asset loading information for debugging
    Global.logAssetInfo();

    // Add listeners to handle loading errors
    this.load.on("loaderror", (file: Phaser.Loader.File) => {
      console.error(
        `Failed to load file '${file.key}' - ${file.xhrLoader?.statusText || "Unknown error"}`,
      );
      console.error("File URL:", file.url);
      // Continue with the game even if some assets fail to load
      this.handleLoadError(file);
    });

    this.load.json("version", "version.json");

    this.load.bitmapFont(
      "fnt_paintBasic",
      Global.getFontPath("fnt_paintBasic.png"),
      Global.getFontPath("fnt_paintBasic.xml"),
    );
  }

  public create(): void {
    this.graphics = this.add.graphics();
    // Set game version text
    const version = this.cache.json.get("version");
    Global.gameVersion = `${version.majorVersion}.${version.minorVersion}.${version.patchVersion}`;
    this.versionText = this.add.bitmapText(
      this.scale.width / 2,
      this.scale.height - 20,
      "fnt_paintBasic",
      `ver. ${Global.gameVersion}`,
      Math.max(Global.minFontSize, 20),
    );
    this.versionText.setOrigin(0.5);

    // Version text background
    this.graphics.clear();
    this.graphics.fillStyle(0x908cc7, 1);
    this.graphics.fillRoundedRect(
      this.versionText.x - this.versionText.displayWidth * 0.55,
      this.versionText.y - this.versionText.displayHeight * 0.55,
      this.versionText.displayWidth * 1.1,
      this.versionText.displayHeight * 1.1,
      this.versionText.displayHeight * 0.4,
    );

    // Create loading bar and text
    this.loadingBar = this.add.graphics();
    this.loadingText = this.add.bitmapText(
      this.scale.width / 2,
      this.scale.height / 2,
      "fnt_paintBasic",
      "0%",
      Math.max(Global.minFontSize * 2, 20),
    );
    this.loadingText.setOrigin(0.5);
    this.drawgraphics();

    this.isReady = true;

    // Track progress on load start and update events
    this.load.on(
      "progress",
      (progress: number) => {
        this.loadingProgress = Phaser.Math.Clamp(progress, 0, 1);
        this.drawgraphics();

        // On complete play an animation with the loading bar and continue to main menu
        if (progress === 1) {
          this.tweens.add({
            targets: this.loadingText,
            y: { from: this.loadingText.y, to: this.scale.height / 2 },
            duration: 500,
            ease: "Power2",
          });

          this.tweens.add({
            targets: [this.loadingBar, this.graphics],
            alpha: { from: 1, to: 0 },
            duration: 500,
            ease: "Power2",
            hold: 500,
            onComplete: () => this.scene.start("MainMenuScene"),
          });
        }
      },
      this,
    );

    // Load remaining assets
    this.loadAssets();
  }

  override repositionElements(width: number, height: number): void {
    if (!this.isReady) return;

    // Redraw the loading bar and reposition loading text
    this.drawgraphics();

    // Position version text
    this.versionText.setPosition(width / 2, height - 40);

    this.graphics.clear();
    this.graphics.fillStyle(0x908cc7, 1);
    this.graphics.fillRoundedRect(
      this.versionText.x - this.versionText.displayWidth * 0.55,
      this.versionText.y - this.versionText.displayHeight * 0.55,
      this.versionText.displayWidth * 1.1,
      this.versionText.displayHeight * 1.1,
      this.versionText.displayHeight * 0.4,
    );
  }

  private drawgraphics(): void {
    const barWidth = this.scale.width * 0.5;
    const barHeight = this.scale.height * 0.01;
    const barX = (this.scale.width - barWidth) / 2;
    const barY = this.scale.height / 2 - barHeight / 2;

    // Define a padding to make the background bar slightly larger
    const barPadding = this.scale.width * 0.005;
    const bgBarX = barX - barPadding;
    const bgBarY = barY - barPadding;
    const bgBarWidth = barWidth + barPadding * 2;
    const bgBarHeight = barHeight + barPadding * 2;

    // Update and reposition the loading percentage text
    this.loadingText.setText(`${Math.floor(this.loadingProgress * 100)}%`);
    this.loadingText.setPosition(
      this.scale.width / 2,
      barY + this.loadingText.displayHeight / 2 + barHeight,
    );

    // Clear and redraw the loading bar
    this.loadingBar.clear();

    // Draw text background
    this.loadingBar.fillStyle(0x908cc7, 1);
    this.loadingBar.fillRoundedRect(
      this.loadingText.x - this.loadingText.displayWidth * 0.75,
      this.loadingText.y - this.loadingText.displayHeight * 0.75,
      this.loadingText.displayWidth * 1.5,
      this.loadingText.displayHeight * 1.5,
      bgBarHeight / 2,
    );

    // Draw the background bar with rounded edges
    this.loadingBar.fillStyle(0x908cc7, 1);
    this.loadingBar.fillRoundedRect(
      bgBarX,
      bgBarY,
      bgBarWidth,
      bgBarHeight,
      bgBarHeight / 2,
    );

    // Draw the progress fill with rounded edges
    this.loadingBar.fillStyle(0xb0cfee, 1);
    this.loadingBar.fillRoundedRect(
      barX,
      barY,
      barWidth * this.loadingProgress,
      barHeight,
      barHeight / 2,
    );
  }

  private loadAssets(): void {
    this.loadTextures();
    this.loadAudio();
    this.loadFonts();

    // Otherwise, run the loader normally
    this.load.start();
  }

  private loadTextures(): void {
    // Load graphics here
    this.load.glsl(
      "shd_menuBackground",
      Global.shaderBaseUrl + "shd_menuBackground.frag",
    );

    this.load.image("gameJamLogo", Global.graphicsBaseUrl + "gameJamLogo.png");
  }

  private loadAudio(): void {
    // Load audio files here
    this.load.audio("main_menu_loop_1", [
      Global.audioBaseUrl + "main_menu/main_menu_loop_1.wav",
    ]);

    this.load.audio("main_menu_loop_2", [
      Global.audioBaseUrl + "main_menu/main_menu_loop_2.wav",
    ]);

    this.load.audio("main_menu_loop_3", [
      Global.audioBaseUrl + "main_menu/main_menu_loop_3.wav",
    ]);
  }

  private loadFonts(): void {
    // Load additional fonts here
  }

  private handleLoadError(file: Phaser.Loader.File): void {
    // Log the error for debugging
    console.warn(`Asset loading failed for: ${file.key}`);

    // You could implement fallback logic here, such as:
    // - Loading a default/placeholder asset
    // - Showing a user-friendly error message
    // - Continuing without the asset if it's not critical

    // For now, we'll just continue with the game
  }
}
